import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { listAssets } from "../lib/generate/listAssets.js";
import { listFolders, createFolder, renameFolder, deleteFolder, setAssetFolder } from "../lib/folders/folders.js";
import { deleteAsset } from "../lib/generate/deleteAsset.js";
import ConfirmDialog from "../Components/ConfirmDialog.js";
import { canEdit } from "../constants.js";
import { LayersIcon } from "../Components/Icons";
import AssetsTable from "./AssetsTable.js";
import FolderRail from "./FolderRail.js";
import "./Assets.css";

export default function Assets({ config }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const brandSlug = searchParams.get("brand");

  const brands = (config?.config ?? []).filter((item) => item.brand.id !== null);
  const entry = brands.find((item) => item.brand.slug === brandSlug) ?? null;
  const editable = canEdit(config.userRole);
  const [brandPickerOpen, setBrandPickerOpen] = useState(false);
  const logoFor = (item) => (item.references ?? []).find((r) => r.kind === "logo")?.url ?? null;

  // Defaults to the first brand when none is selected yet via the URL -
  // e.g. landing on /assets directly, rather than arriving with a brand
  // already chosen from elsewhere. Uses replace so this default doesn't
  // add a confusing extra entry to browser back-history.
  useEffect(() => {
    if (!brandSlug && brands.length > 0) {
      setSearchParams({ brand: brands[0].brand.slug }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandSlug, brands.length]);

  const [assets, setAssets] = useState([]);
  const [folders, setFolders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [actionError, setActionError] = useState(null);

  useEffect(() => {
    if (!entry) return;
    let cancelled = false;
    setIsLoading(true);
    setLoadError(null);
    setActionError(null);

    (async () => {
      try {
        const [assetList, folderList] = await Promise.all([
          listAssets(config, entry.brand.id, () => {}, () => {}),
          listFolders(config, entry.brand.id, () => {}, () => {}),
        ]);
        if (!cancelled) {
          setAssets(assetList);
          setFolders(folderList);
        }
      } catch (err) {
        if (!cancelled) setLoadError("Couldn't load assets. Please try again.");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry?.brand.id]);

  function handleBrandChange(slug) {
    setSearchParams(slug ? { brand: slug } : {});
    setAssets([]);
    setFolders([]);
    setBrandPickerOpen(false);
  }

  // Folder create/rename/delete -------------------------------------------
  // FolderRail owns its own dialog UI state (name input, saving, error)
  // internally now - these are thin wrappers around the lib calls,
  // returning/throwing so FolderRail can surface a real error message
  // rather than assuming success.
  const [activeFolder, setActiveFolder] = useState("all");

  async function handleCreateFolder(name) {
    if (!entry) return;
    const next = await createFolder(config, entry.brand.id, name, () => {}, () => {});
    setFolders(next);
  }

  async function handleRenameFolder(folderId, name) {
    if (!entry) return;
    const next = await renameFolder(config, entry.brand.id, folderId, name, () => {}, () => {});
    setFolders(next);
  }

  async function handleDeleteFolder(folder) {
    if (!entry) return;
    const next = await deleteFolder(config, entry.brand.id, folder.id, () => {}, () => {});
    setFolders(next);
    // The deleted folder can't still be the active filter afterward.
    if (activeFolder === folder.id) setActiveFolder("all");
  }

  async function handleMoveToFolder(assetId, folderId) {
    if (!entry) return;
    setActionError(null);
    try {
      const updated = await setAssetFolder(config, entry.brand.id, assetId, folderId, () => {}, () => {});
      setAssets((current) => current.map((a) => (a.id === assetId ? updated : a)));
    } catch (err) {
      setActionError(err.message);
    }
  }

  const [deletingAssetId, setDeletingAssetId] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(null); // the asset pending a confirm/cancel choice

  async function handleDeleteAsset() {
    if (!entry || !confirmingDelete) return;
    const asset = confirmingDelete;
    setActionError(null);
    setDeletingAssetId(asset.id);
    try {
      await deleteAsset(config, { brandId: entry.brand.id, assetId: asset.id }, () => {}, () => {});
      setAssets((current) => current.filter((a) => a.id !== asset.id));
      setConfirmingDelete(null);
    } catch (err) {
      setActionError(err.message);
    } finally {
      setDeletingAssetId(null);
    }
  }

  return (
    <div className="assets-page">
      <div className="assets-page__header">
        <div>
          <h1 className="assets-page__title">Assets</h1>
          <p className="assets-page__subtitle">Pick a brand to see its banners and folders.</p>
        </div>

        <div className="assets-page__brand-picker">
          <button
            type="button"
            onClick={() => setBrandPickerOpen((o) => !o)}
            aria-expanded={brandPickerOpen}
            aria-label="Select brand"
            className="assets-page__brand-picker-btn"
          >
            {entry ? (
              <>
                {logoFor(entry) ? (
                  <img src={logoFor(entry)} alt="" className="assets-page__brand-picker-logo" />
                ) : (
                  <span className="assets-page__brand-picker-logo assets-page__brand-picker-logo--placeholder" />
                )}
                <span>{entry.brand.name}</span>
              </>
            ) : (
              <span className="assets-page__brand-picker-placeholder">Select a brand…</span>
            )}
            <span className="assets-page__brand-picker-chevron" aria-hidden>
              ▾
            </span>
          </button>

          {brandPickerOpen ? (
            <>
              <div className="assets-page__brand-picker-scrim" onClick={() => setBrandPickerOpen(false)} aria-hidden />
              <div className="assets-page__brand-picker-menu">
                {brands.map((item) => (
                  <button
                    key={item.brand.slug}
                    type="button"
                    onClick={() => handleBrandChange(item.brand.slug)}
                    className={`assets-page__brand-picker-item${
                      item.brand.slug === brandSlug ? " assets-page__brand-picker-item--active" : ""
                    }`}
                  >
                    {logoFor(item) ? (
                      <img src={logoFor(item)} alt="" className="assets-page__brand-picker-logo" />
                    ) : (
                      <span className="assets-page__brand-picker-logo assets-page__brand-picker-logo--placeholder" />
                    )}
                    <span>{item.brand.name}</span>
                  </button>
                ))}
              </div>
            </>
          ) : null}
        </div>
      </div>

      {!entry ? (
        <div className="assets-page__empty">
          <span className="assets-page__empty-icon">
            <LayersIcon className="assets-page__empty-icon-svg" />
          </span>
          <h2 className="assets-page__empty-title">Pick a brand to get started</h2>
          <p className="assets-page__empty-desc">
            Select a brand above to see its banners, organize them into folders, and jump
            straight into editing any of them.
          </p>
        </div>
      ) : isLoading ? (
        <p className="assets-page__status">Loading…</p>
      ) : loadError ? (
        <p className="assets-page__status assets-page__status--error">{loadError}</p>
      ) : (
        <>
          {!editable ? <p className="assets-page__role-notice">Editors and admins only.</p> : null}
          {actionError ? <p className="assets-page__action-error">{actionError}</p> : null}

          <FolderRail
            folders={folders}
            assets={assets}
            activeFolder={activeFolder}
            onSelectFolder={setActiveFolder}
            onCreateFolder={handleCreateFolder}
            onRenameFolder={handleRenameFolder}
            onDeleteFolder={handleDeleteFolder}
            readOnly={!editable}
          />

          <AssetsTable
            assets={assets}
            brandSlug={entry.brand.slug}
            folders={folders}
            activeFolder={activeFolder}
            onMoveToFolder={handleMoveToFolder}
            onDelete={setConfirmingDelete}
            deletingId={deletingAssetId}
            editable={editable}
          />
        </>
      )}

      {confirmingDelete ? (
        <ConfirmDialog
          title={`Delete ${confirmingDelete.publicAssetCode ?? confirmingDelete.id}?`}
          message="This can't be undone."
          confirmLabel="Delete"
          danger
          isBusy={deletingAssetId === confirmingDelete.id}
          onConfirm={handleDeleteAsset}
          onCancel={() => setConfirmingDelete(null)}
        />
      ) : null}
    </div>
  );
}